import { License } from "@/pages/settings/components/settingsItems/hooks/types";
import {
  AccountSetting,
  CurrenciesResponse,
  CurrencyRatesResponse,
  CurrencyUsageResponse,
  ProductsResponse,
  SettingsIndustriesResponse,
  SettingsThresholds,
  SubIndustriesResponse,
  TokensData,
  TokenStatus,
  WorkflowScoreStatusesResponse
} from "@/entities/settings/types";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import { ExtendedUseQueryResult } from "@/services/api/types";
import useBoundStore from "@/services/store/BoundStore";
import { useEffect, useMemo } from "react";
import { ServiceItem } from "@/pages/settings/pages/products-v2/types";
import { Service } from "@/pages/settings/components/connectedAccounts/types";
import apiRoutes from "@/services/api/routes";

export const LICENSE_TYPE_ID = 1;

export const useThresholds = () => {
  const thresholds = useBoundStore((state) => state.settings.thresholds);
  const setThresholds = useBoundStore((state) => state.settings.setThresholds);

  const hasCachedThresholds = Object.keys(thresholds).length > 0;
  const thresholdsList = useMemo(() => Object.values(thresholds), [thresholds]);

  const { data, error, isLoading, refetch } = useAPI(
    "settings/thresholds/all",
    {},
    {
      retry: false,
      enabled: !hasCachedThresholds
    }
  );

  useEffect(() => {
    if (data?.thresholds && !hasCachedThresholds) {
      setThresholds(data.thresholds);
    }
  }, [data?.thresholds, hasCachedThresholds, setThresholds]);

  if (hasCachedThresholds) {
    return {
      thresholds: thresholdsList,
      isLoading: false,
      error: null,
      refetch
    };
  }

  return {
    thresholds: thresholdsList,
    isLoading,
    error,
    refetch
  };
};

export const useThresholdById = (
  thresholdId: string
): ExtendedUseQueryResult<SettingsThresholds> => {
  const thresholds = useBoundStore((state) => state.settings.thresholds);
  const setThresholds = useBoundStore((state) => state.settings.setThresholds);

  const cachedThreshold = thresholds[thresholdId];

  const apiResult = useAPI(
    "settings/thresholds/byId",
    { pathParams: { thresholdsId: thresholdId } },
    {
      enabled: !cachedThreshold // Only fetch if not in store
    }
  );

  useEffect(() => {
    if (apiResult.isSuccess && apiResult.data && !cachedThreshold) {
      // Add the fetched threshold to the store
      setThresholds([apiResult.data]);
    }
  }, [apiResult.data, apiResult.isSuccess, cachedThreshold, setThresholds]);

  // Return cached data if available
  if (cachedThreshold) {
    return {
      ...apiResult,
      data: cachedThreshold,
      isSuccess: true,
      isLoading: false,
      isFetching: false
    } as ExtendedUseQueryResult<SettingsThresholds>;
  }

  return apiResult;
};

export const useUpdateThresholdMedium = (thresholdId: string) => {
  const setThresholds = useBoundStore((state) => state.settings.setThresholds);

  return useAPIMutation<
    {
      data: {
        medium: number;
      };
    },
    SettingsThresholds
  >(
    "settings/thresholds/update",
    {
      method: "PUT",
      pathParams: { thresholdsId: thresholdId }
    },
    (updatedThreshold: SettingsThresholds) => {
      setThresholds([updatedThreshold]);
    }
  );
};

export const useUpdateThresholdHigh = (thresholdId: string) => {
  const setThresholds = useBoundStore((state) => state.settings.setThresholds);

  return useAPIMutation<
    {
      data: {
        high: number;
      };
    },
    SettingsThresholds
  >(
    "settings/thresholds/update",
    {
      method: "PUT",
      pathParams: { thresholdsId: thresholdId }
    },
    (updatedThreshold: SettingsThresholds) => {
      setThresholds([updatedThreshold]);
    }
  );
};

export const useAccountSettings = (): ExtendedUseQueryResult<
  AccountSetting[]
> => {
  const accountSettings = useBoundStore((state) => state.settings.accountSettings);
  const setAccountSettings = useBoundStore((state) => state.settings.setAccountSettings);

  const hasSettings = Object.keys(accountSettings).length > 0;
  const accountSettingsList = useMemo(() => Object.values(accountSettings), [accountSettings]);

  const apiResult = useAPI("settings/account", {
    pathParams: { settingKey: "" }
  });

  useEffect(() => {
    if (apiResult.isSuccess && apiResult.data) {
      setAccountSettings(apiResult.data);
    }
  }, [apiResult.data, apiResult.isSuccess, setAccountSettings]);

  // Return cached data if available
  if (hasSettings) {
    return {
      ...apiResult,
      data: accountSettingsList,
      isSuccess: true,
      isLoading: false,
      isFetching: false
    } as ExtendedUseQueryResult<AccountSetting[]>;
  }

  return apiResult;
};

export const useAccountSettingByKey = (
  settingKey: string
): ExtendedUseQueryResult<AccountSetting> => {
  const accountSettings = useBoundStore((state) => state.settings.accountSettings);
  const setAccountSetting = useBoundStore((state) => state.settings.setAccountSetting);

  const cachedSetting = accountSettings[settingKey];

  const apiResult = useAPI(
    "settings/account",
    { pathParams: { settingKey } },
    {
      enabled: !cachedSetting // Only fetch if not in store
    }
  );

  useEffect(() => {
    if (apiResult.isSuccess && apiResult.data && !cachedSetting) {
      setAccountSetting(apiResult.data);
    }
  }, [apiResult.data, apiResult.isSuccess, cachedSetting, setAccountSetting]);

  // Return cached data if available
  if (cachedSetting) {
    return {
      ...apiResult,
      data: cachedSetting,
      isSuccess: true,
      isLoading: false,
      isFetching: false
    } as ExtendedUseQueryResult<AccountSetting>;
  }

  return apiResult;
};

export const useUpdateAccountSettingByKey = () => {
  const queryClient = useQueryClient();
  const setAccountSetting = useBoundStore((state) => state.settings.setAccountSetting);

  return useAPIMutation<
    {
      pathParams: {
        settingKey: string;
      };
      data: Partial<AccountSetting>;
    },
    AccountSetting
  >(
    "settings/account",
    {
      method: "PUT"
    },
    (updatedSetting: AccountSetting) => {
      setAccountSetting(updatedSetting);
      queryClient.invalidateQueries({ queryKey: ["settings/account"] });
    }
  );
};

export const useLicenseServiceItems = (): {
  licenseItems: ServiceItem[];
  isLoading: boolean;
} => {
  const { data: productsData, isLoading } = useProducts();

  const licenseItems = useMemo(() => {
    if (!productsData?.products) return [];
    return productsData.products.flatMap((product) =>
      (product.serviceItems ?? [])
        .filter((item) => item.typeId === LICENSE_TYPE_ID)
        .map((item) => ({
          ...item,
          productId: item.productId || product.id
        }))
    );
  }, [productsData?.products]);

  return { licenseItems, isLoading };
};

export const useLicenseServiceItemsByProduct = (
  productId: string | null | undefined
): {
  licenseItems: ServiceItem[];
  isLoading: boolean;
} => {
  const { data: productsData, isLoading } = useProducts();

  const licenseItems = useMemo(() => {
    if (!productId || !productsData?.products) return [];
    const product = productsData.products.find((p) => p.id === productId);
    if (!product?.serviceItems) return [];
    return product.serviceItems
      .filter((item) => item.typeId === LICENSE_TYPE_ID)
      .map((item) => ({
        ...item,
        productId: item.productId || product.id
      }));
  }, [productsData?.products, productId]);

  return { licenseItems, isLoading };
};

export const useCurrencies = (): ExtendedUseQueryResult<CurrenciesResponse> =>
  useAPI("settings/currencies");

export const useCurrencyRates = (
  baseCurrency?: string
): ExtendedUseQueryResult<CurrencyRatesResponse> =>
  useAPI(
    "settings/currencyRates",
    baseCurrency
      ? {
          pathParams: {
            currencyCode: baseCurrency
          }
        }
      : {}
  );

/**
 * Whether the account holds any record signed in a currency other than its own.
 * Drives the default visibility of the terms-currency columns: an account that
 * has only ever signed in its system currency would see the same number in
 * both halves of every split pair.
 *
 * The backend answers `true` when it cannot tell — an indeterminate read must
 * offer the columns rather than silently withhold them.
 */
export const useCurrencyUsage = (): ExtendedUseQueryResult<CurrencyUsageResponse> =>
  useAPI("settings/currencyUsage");

export const useProducts = (): ExtendedUseQueryResult<ProductsResponse> => {
  return useAPI("settings/products", {});
};

export const useUpdateLicense = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    {
      pathParams: {
        licenseId: string;
      };
      data: Partial<License>;
    },
    License
  >(
    "settings/licenses",
    {
      method: "PUT"
    },
    () => {
      queryClient.invalidateQueries({ queryKey: ["settings/products"] });
    }
  );
};

export const useSubIndustries =
  (): ExtendedUseQueryResult<SubIndustriesResponse> => {
    const storedSubIndustries = useBoundStore((state) =>
      Object.values(state.subIndustries.entities)
    );
    const addSubIndustry = useBoundStore(
      (state) => state.subIndustries.addEntity
    );

    const result = useAPI(
      "settings/subIndustries",
      {},
      {
        enabled: storedSubIndustries.length === 0
      }
    );

    useEffect(() => {
      if (result.isSuccess && result.data && storedSubIndustries.length === 0) {
        result.data.forEach(addSubIndustry);
      }
    }, [
      result.data,
      result.isSuccess,
      addSubIndustry,
      storedSubIndustries.length
    ]);

    if (storedSubIndustries.length > 0) {
      return {
        ...result,
        data: storedSubIndustries,
        isSuccess: true,
        isLoading: false,
        isFetching: false
      } as ExtendedUseQueryResult<SubIndustriesResponse>;
    }

    return result;
  };

export const useSubIndustriesByIndustryId = (
  industryId: number | null
): ExtendedUseQueryResult<SubIndustriesResponse> => {
  return useAPI(
    "settings/subIndustriesByIndustry",
    {
      pathParams: {
        industryId: industryId === null ? "" : industryId?.toString()
      }
    },
    { enabled: industryId !== null }
  );
};

// settingsapi industries with visibility/system flags (envelope response),
// for the configurable-system-field editor. Distinct from the companies-service
// useCompanyIndustries lookup, which returns id/name only.
export const useIndustries = (
  enabled = true
): ExtendedUseQueryResult<SettingsIndustriesResponse> => {
  return useAPI("settings/industries", {}, { enabled });
};

export const useTokenStatus = (
  serviceId: string
): ExtendedUseQueryResult<TokenStatus> => {
  return useAPI(
    "settings/tokens",
    {
      pathParams: { serviceId }
    },
    {
      enabled: !!serviceId,
      retry: false
    }
  );
};

// Services whose connector also links the workspace to the account must be
// disconnected through the connector, which removes the token and the link.
const SERVICE_DISCONNECT_ROUTES: Partial<
  Record<string, keyof typeof apiRoutes>
> = {
  [Service.SLACK]: "disconnectSlack",
  [Service.ZOOM]: "disconnectZoom",
  [Service.ZOOMINFO]: "disconnectZoomInfo"
};
const DISCONNECT_OPTIONS = { method: "DELETE" } as const;

export const useDisconnectService = (serviceId: string) => {
  const disconnectRoute = SERVICE_DISCONNECT_ROUTES[serviceId];
  const mutation = useAPIMutation<
    { pathParams: { serviceId: string } } | Record<string, never>,
    void
  >(disconnectRoute ?? "settings/tokens", DISCONNECT_OPTIONS);

  return {
    ...mutation,
    disconnect: () =>
      mutation.mutateAsync(disconnectRoute ? {} : { pathParams: { serviceId } })
  };
};

export const useAllQuestionnaires = () => {
  return useAPI("settings/questionnaireAll", {
    method: "GET"
  });
};

export const useCreateQuestionnaireMutation = () => {
  return useAPIMutation<
    {
      data: {
        name: string;
        description?: string;
        stageId: number;
        type: number;
        kpiValue?: string;
        questionnaireOrder?: number;
        isGuardrails?: boolean;
        isSuccessCriteriaEnabled?: boolean;
      };
    },
    any
  >("questionnaires/create", {
    method: "POST"
  });
};

export const useSettingsTokens = (): ExtendedUseQueryResult<TokensData> => {
  return useAPI("settings/tokens/all", {
    method: "GET"
  });
};

export const useDriveConnectorStatus = (): ExtendedUseQueryResult<{
  oauthConfigured: boolean;
}> => {
  return useAPI("driveconnector/status", {
    method: "GET"
  });
};

export const useWorkflowScoreStatuses =
  (): ExtendedUseQueryResult<WorkflowScoreStatusesResponse> => {
    return useAPI("settings/contracts/workflowScoreStatuses", {});
  };
